// Where a floating panel (tooltip, menu) goes next to its trigger. Pure: no imports, so node --test loads it.

export type PlacementSide = 'top' | 'bottom' | 'left' | 'right';
export type PlacementAlign = 'start' | 'center' | 'end';

type Box = {
    top: number;
    left: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
};
type Size = { width: number; height: number };

export type Placement = {
    side: PlacementSide;
    /** Along the trigger's edge: `start` lines the panel up with its top / left, `end` with its bottom / right. */
    align: PlacementAlign;
};

/**
 * Fixed coordinates for the panel's top-left corner. The panel goes on `placement.side` and flips to the
 * opposite one when it does not fit there but fits opposite; then it is clamped into the viewport on both
 * axes. `side` in the result is the side it ended up on.
 */
export function computePlacement(
    trigger: Box,
    panel: Size,
    placement: Placement,
    viewport: Size,
    options: { gap?: number; margin?: number } = {}
): { top: number; left: number; side: PlacementSide } {
    const { gap = 8, margin = 8 } = options;
    const fits: Record<PlacementSide, boolean> = {
        top: trigger.top - gap - panel.height >= margin,
        bottom: trigger.bottom + gap + panel.height <= viewport.height - margin,
        left: trigger.left - gap - panel.width >= margin,
        right: trigger.right + gap + panel.width <= viewport.width - margin
    };
    const opposite: Record<PlacementSide, PlacementSide> = {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left'
    };
    const side = !fits[placement.side] && fits[opposite[placement.side]] ? opposite[placement.side] : placement.side;

    const alongX = (): number =>
        placement.align === 'start'
            ? trigger.left
            : placement.align === 'end'
              ? trigger.right - panel.width
              : trigger.left + trigger.width / 2 - panel.width / 2;
    const alongY = (): number =>
        placement.align === 'start'
            ? trigger.top
            : placement.align === 'end'
              ? trigger.bottom - panel.height
              : trigger.top + trigger.height / 2 - panel.height / 2;

    const top = side === 'top' ? trigger.top - gap - panel.height : side === 'bottom' ? trigger.bottom + gap : alongY();
    const left = side === 'left' ? trigger.left - gap - panel.width : side === 'right' ? trigger.right + gap : alongX();

    return {
        side,
        top: clamp(top, margin, viewport.height - panel.height - margin),
        left: clamp(left, margin, viewport.width - panel.width - margin)
    };
}

/** Like Math.min/max, but the lower bound wins when the range is empty (panel larger than the viewport). */
function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(value, max));
}
